<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import AdvisorAPI from 'dashboard/api/teletel/advisor';
import PageHeader from 'dashboard/components-next/teletel/PageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AdvisorPlanCard from './AdvisorPlanCard.vue';
import AdvisorCapabilities from './AdvisorCapabilities.vue';
import { useAdvisorVoice } from './useAdvisorVoice';

// «مستشار المدير»: a chat that explains how to organise the account and
// suggests settings, answered by Teletel's AI from the manager-guide articles
// and read-only lookups of this account. Since phase 2 (2026-09-19) an answer
// may carry a plan of allowed actions that runs only after the manager
// approves it here, and a question can be spoken. The conversation lives in
// this browser only.
const POLL_EVERY = 2000;
const POLL_FOR = 90000;
const PLAN_POLL_FOR = 300000;
const HISTORY_TURNS = 8;
const PROMPT_KEYS = [
  'RESTRICT',
  'FAIR',
  'ESCALATE',
  'AUTOMATION_OR_FLOW',
  'SETUP',
  'AUDIT',
];
const VOICE_NOTICES = {
  VOICE_BROWSER_FALLBACK: 'VOICE_BROWSER_FALLBACK',
  VOICE_EMPTY: 'VOICE_EMPTY',
  MIC_DENIED: 'MIC_DENIED',
  NO_VOICE: 'NO_VOICE',
  VOICE_FAILED: 'VOICE_FAILED',
};

const { t, locale } = useI18n();
const route = useRoute();

const messages = ref([]);
const draft = ref('');
const isThinking = ref(false);
const error = ref('');
const notice = ref('');
const planBusy = ref(false);
const showCapabilities = ref(false);
const listRef = ref(null);
const textareaRef = ref(null);
let poller = null;
let pollDeadline = 0;
const planPollers = new Map();

const storageKey = computed(() => `teletel-advisor-${route.params.accountId}`);
const hasMessages = computed(() => messages.value.length > 0);
const formatted = content =>
  new MessageFormatter(content || '').formattedMessage;
const speakable = item =>
  [item.content, ...(item.steps || [])].filter(Boolean).join('. ');

const persist = () => {
  try {
    localStorage.setItem(
      storageKey.value,
      JSON.stringify(messages.value.slice(-30))
    );
  } catch (e) {
    // storage blocked: the chat just does not survive a reload
  }
};

const restore = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey.value) || '[]');
    if (Array.isArray(saved)) messages.value = saved;
  } catch (e) {
    messages.value = [];
  }
};

const scrollToBottom = async () => {
  await nextTick();
  if (listRef.value) listRef.value.scrollTop = listRef.value.scrollHeight;
};

const stopPolling = () => {
  if (poller) clearTimeout(poller);
  poller = null;
  isThinking.value = false;
};

const stopPlanPolling = id => {
  const handle = planPollers.get(id);
  if (handle) clearTimeout(handle);
  planPollers.delete(id);
};

// What happened to a plan travels with the answer it came with, so the
// advisor knows what ran when the manager asks where things stand.
const planNote = plan => {
  if (!plan) return '';
  const status = t(`TELETEL_ADVISOR.PLAN.STATUS.${plan.status.toUpperCase()}`);
  const steps = (plan.steps || [])
    .map(step => `${step.summary} (${step.state})`)
    .join('؛ ');
  return `\n[الخطة: ${status}: ${steps}]`;
};

const history = () =>
  messages.value
    .filter(m => m.content)
    .slice(-HISTORY_TURNS)
    .map(m => ({ role: m.role, content: `${m.content}${planNote(m.plan)}` }));

const setPlan = (id, plan) => {
  const item = messages.value.find(m => m.plan && m.plan.id === id);
  if (item) item.plan = plan;
  persist();
};

// A plan's progress is read from the server until it settles; a reload
// picks up any plan still running.
const pollPlan = async (id, deadline) => {
  try {
    const { data } = await AdvisorAPI.plan(id);
    const plan = data.payload;
    setPlan(id, plan);
    if (!['running', 'undoing'].includes(plan.status)) {
      stopPlanPolling(id);
      return;
    }
  } catch (e) {
    stopPlanPolling(id);
    return;
  }
  if (Date.now() > deadline) {
    stopPlanPolling(id);
    return;
  }
  planPollers.set(
    id,
    setTimeout(() => pollPlan(id, deadline), POLL_EVERY)
  );
};

const watchPlan = id => {
  stopPlanPolling(id);
  pollPlan(id, Date.now() + PLAN_POLL_FOR);
};

const pushAnswer = payload => {
  messages.value.push({
    id: Date.now(),
    role: 'assistant',
    content: payload.answer || '',
    steps: payload.steps || [],
    links: payload.links || [],
    lookups: payload.lookups || [],
    plan: payload.plan || null,
    planError: payload.plan_error || '',
  });
  persist();
};

const poll = async ticket => {
  try {
    const { data } = await AdvisorAPI.status(ticket);
    const payload = data.payload || {};
    if (payload.status === 'done') {
      stopPolling();
      pushAnswer(payload);
      return;
    }
    if (payload.status === 'error') {
      stopPolling();
      error.value = payload.error || t('TELETEL_ADVISOR.ERROR');
      return;
    }
  } catch (e) {
    stopPolling();
    error.value = e?.response?.data?.error || t('TELETEL_ADVISOR.ERROR');
    return;
  }
  if (Date.now() > pollDeadline) {
    stopPolling();
    error.value = t('TELETEL_ADVISOR.TIMEOUT');
    return;
  }
  poller = setTimeout(() => poll(ticket), POLL_EVERY);
};

const send = async text => {
  const question = (text ?? draft.value).trim();
  if (!question || isThinking.value) return;
  error.value = '';
  notice.value = '';
  const priorHistory = history();
  messages.value.push({ id: Date.now(), role: 'user', content: question });
  persist();
  draft.value = '';
  isThinking.value = true;
  try {
    const { data } = await AdvisorAPI.ask(question, priorHistory);
    pollDeadline = Date.now() + POLL_FOR;
    poller = setTimeout(() => poll(data.payload.ticket), POLL_EVERY);
  } catch (e) {
    stopPolling();
    error.value = e?.response?.data?.error || t('TELETEL_ADVISOR.ERROR');
  }
};

const approvePlan = async ({ id, confirmSensitive }) => {
  planBusy.value = true;
  error.value = '';
  try {
    const { data } = await AdvisorAPI.approvePlan(id, confirmSensitive);
    setPlan(id, data.payload);
    watchPlan(id);
  } catch (e) {
    error.value = e?.response?.data?.error || t('TELETEL_ADVISOR.ERROR');
  } finally {
    planBusy.value = false;
  }
};

const undoPlan = async id => {
  planBusy.value = true;
  error.value = '';
  try {
    const { data } = await AdvisorAPI.undoPlan(id);
    setPlan(id, data.payload);
    watchPlan(id);
  } catch (e) {
    error.value = e?.response?.data?.error || t('TELETEL_ADVISOR.ERROR');
  } finally {
    planBusy.value = false;
  }
};

const autosize = () => {
  const el = textareaRef.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
};

const voice = useAdvisorVoice({
  locale: () => locale.value,
  onText: async text => {
    draft.value = draft.value ? `${draft.value} ${text}` : text;
    notice.value = '';
    await nextTick();
    autosize();
    textareaRef.value?.focus();
  },
  onError: code => {
    notice.value = VOICE_NOTICES[code]
      ? t(`TELETEL_ADVISOR.VOICE.${VOICE_NOTICES[code]}`)
      : code;
  },
});

const reset = () => {
  stopPolling();
  planPollers.forEach((_, id) => stopPlanPolling(id));
  voice.stopSpeaking();
  messages.value = [];
  error.value = '';
  notice.value = '';
  persist();
};

const onEnter = event => {
  if (event.isComposing) return;
  event.preventDefault();
  send();
};

watch(() => [messages.value.length, isThinking.value], scrollToBottom);
onMounted(() => {
  restore();
  messages.value
    .filter(
      m => m.plan && ['running', 'undoing', 'scheduled'].includes(m.plan.status)
    )
    .forEach(m => watchPlan(m.plan.id));
  scrollToBottom();
});
onBeforeUnmount(() => {
  stopPolling();
  planPollers.forEach((_, id) => stopPlanPolling(id));
});
</script>

<template>
  <div class="flex w-full flex-col gap-6">
    <PageHeader
      :title="t('TELETEL_ADVISOR.HEADER')"
      :description="t('TELETEL_ADVISOR.DESCRIPTION')"
    >
      <template #actions>
        <NextButton
          slate
          faded
          sm
          icon="i-lucide-shield-check"
          :label="t('TELETEL_ADVISOR.CAPABILITIES.BUTTON')"
          @click="showCapabilities = !showCapabilities"
        />
        <NextButton
          v-if="hasMessages"
          slate
          faded
          sm
          icon="i-lucide-refresh-ccw"
          :label="t('TELETEL_ADVISOR.RESET')"
          @click="reset"
        />
      </template>
    </PageHeader>

    <AdvisorCapabilities
      v-if="showCapabilities"
      @close="showCapabilities = false"
    />

    <div
      class="flex h-[calc(100vh-16rem)] min-h-[28rem] flex-col rounded-xl border border-n-weak bg-n-solid-1"
    >
      <div ref="listRef" class="flex-1 overflow-y-auto px-4 py-4 text-sm">
        <div v-if="!hasMessages" class="flex flex-col items-center gap-3 pt-6">
          <span class="i-lucide-compass size-9 text-n-blue-11" />
          <p class="max-w-xl text-center text-n-slate-11">
            {{ t('TELETEL_ADVISOR.INTRO') }}
          </p>
          <button
            v-if="!showCapabilities"
            type="button"
            class="inline-flex items-center gap-1 text-xs text-n-blue-11 hover:underline"
            @click="showCapabilities = true"
          >
            <span class="i-lucide-shield-check size-3.5" />
            {{ t('TELETEL_ADVISOR.CAPABILITIES.BUTTON') }}
          </button>
          <div
            class="mt-2 grid w-full max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2"
          >
            <button
              v-for="key in PROMPT_KEYS"
              :key="key"
              type="button"
              class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-start text-n-slate-12 hover:bg-n-alpha-2"
              @click="send(t(`TELETEL_ADVISOR.PROMPTS.${key}`))"
            >
              {{ t(`TELETEL_ADVISOR.PROMPTS.${key}`) }}
            </button>
          </div>
        </div>

        <div v-else class="mx-auto flex max-w-3xl flex-col gap-5">
          <div
            v-for="item in messages"
            :key="item.id"
            class="flex flex-col gap-1"
          >
            <template v-if="item.role === 'user'">
              <span class="text-xs font-medium text-n-slate-11">
                {{ t('TELETEL_ADVISOR.YOU') }}
              </span>
              <p class="whitespace-pre-wrap break-words">{{ item.content }}</p>
            </template>
            <template v-else>
              <span
                class="flex items-center gap-1 text-xs font-medium text-n-blue-11"
              >
                <span class="i-lucide-compass size-3" />
                {{ t('TELETEL_ADVISOR.ADVISOR') }}
                <button
                  type="button"
                  class="ms-auto inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-n-slate-11 hover:bg-n-alpha-2"
                  :aria-label="t('TELETEL_ADVISOR.VOICE.LISTEN')"
                  :title="t('TELETEL_ADVISOR.VOICE.LISTEN')"
                  @click="voice.speak(item.id, speakable(item))"
                >
                  <span
                    class="size-3.5"
                    :class="
                      voice.speakingId.value === item.id
                        ? 'i-lucide-square'
                        : 'i-lucide-volume-2'
                    "
                  />
                  {{
                    t(
                      voice.speakingId.value === item.id
                        ? 'TELETEL_ADVISOR.VOICE.STOP'
                        : 'TELETEL_ADVISOR.VOICE.LISTEN'
                    )
                  }}
                </button>
              </span>
              <span
                v-if="item.lookups && item.lookups.length"
                class="inline-flex w-fit items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-11"
              >
                <span class="i-lucide-database size-3" />
                {{
                  t('TELETEL_ADVISOR.LOOKUPS_USED', { n: item.lookups.length })
                }}
              </span>
              <div
                v-dompurify-html="formatted(item.content)"
                class="prose-sm break-words"
              />
              <ol
                v-if="item.steps && item.steps.length"
                class="ltr:pl-5 rtl:pr-5 mt-1 flex list-decimal flex-col gap-1 rounded-lg border border-n-weak bg-n-solid-2 p-3 pe-3"
              >
                <li v-for="(step, i) in item.steps" :key="i">{{ step }}</li>
              </ol>
              <div
                v-if="item.links && item.links.length"
                class="mt-1 flex flex-wrap gap-2"
              >
                <a
                  v-for="link in item.links"
                  :key="link.path"
                  :href="link.path"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="inline-flex items-center gap-1 rounded-lg border border-n-weak px-2.5 py-1 text-xs text-n-blue-11 hover:bg-n-alpha-2"
                >
                  <span class="i-lucide-arrow-up-left size-3" />
                  {{ link.label }}
                </a>
              </div>
              <AdvisorPlanCard
                v-if="item.plan"
                :plan="item.plan"
                :busy="planBusy"
                @approve="approvePlan"
                @undo="undoPlan"
              />
              <p
                v-else-if="item.planError"
                class="mt-1 rounded-lg border border-n-amber-7 bg-n-amber-2 px-3 py-2 text-xs text-n-amber-12"
              >
                {{
                  t('TELETEL_ADVISOR.PLAN.REJECTED', { reason: item.planError })
                }}
              </p>
            </template>
          </div>

          <div
            v-if="isThinking"
            class="flex items-center gap-2 text-xs text-n-slate-11"
          >
            <span class="flex gap-1">
              <span
                class="size-1.5 animate-bounce rounded-full bg-n-blue-9 [animation-delay:-0.3s]"
              />
              <span
                class="size-1.5 animate-bounce rounded-full bg-n-blue-9 [animation-delay:-0.15s]"
              />
              <span class="size-1.5 animate-bounce rounded-full bg-n-blue-9" />
            </span>
            {{ t('TELETEL_ADVISOR.THINKING') }}
          </div>
          <p v-if="error" class="text-xs text-n-ruby-11">{{ error }}</p>
        </div>
      </div>

      <form class="relative mx-3 mb-3 mt-1" @submit.prevent="send()">
        <textarea
          ref="textareaRef"
          v-model="draft"
          rows="1"
          :placeholder="
            voice.isRecording.value
              ? t('TELETEL_ADVISOR.VOICE.LISTENING')
              : t('TELETEL_ADVISOR.ASK')
          "
          class="reset-base mb-0 w-full resize-none overflow-hidden rounded-lg border border-n-weak bg-n-alpha-3 py-3 pe-24 ps-4 text-sm text-n-slate-12 focus:border-n-blue-11 focus:outline-none focus:ring-2 focus:ring-n-blue-11"
          @input="autosize"
          @keydown.enter.exact="onEnter"
        />
        <button
          v-if="voice.canRecord()"
          type="button"
          class="absolute bottom-2 end-12 grid size-8 place-content-center rounded-lg disabled:opacity-50"
          :class="
            voice.isRecording.value
              ? 'bg-n-ruby-9 text-white animate-pulse'
              : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
          "
          :disabled="isThinking || voice.isTranscribing.value"
          :aria-label="
            t(
              voice.isRecording.value
                ? 'TELETEL_ADVISOR.VOICE.STOP_RECORDING'
                : 'TELETEL_ADVISOR.VOICE.RECORD'
            )
          "
          :title="
            t(
              voice.isRecording.value
                ? 'TELETEL_ADVISOR.VOICE.STOP_RECORDING'
                : 'TELETEL_ADVISOR.VOICE.RECORD'
            )
          "
          @click="voice.toggle"
        >
          <span
            class="size-4"
            :class="
              voice.isTranscribing.value
                ? 'i-lucide-loader-circle animate-spin'
                : voice.isRecording.value
                  ? 'i-lucide-square'
                  : 'i-lucide-mic'
            "
          />
        </button>
        <button
          type="submit"
          class="absolute bottom-2 end-2 grid size-8 place-content-center rounded-lg bg-n-blue-9 text-white disabled:opacity-50"
          :disabled="isThinking || !draft.trim()"
          :aria-label="t('TELETEL_ADVISOR.SEND')"
        >
          <span class="i-lucide-send size-4" />
        </button>
      </form>
      <p v-if="notice" class="mx-4 mb-1 text-xs text-n-amber-11">
        {{ notice }}
      </p>
      <p class="mx-4 mb-3 text-xs text-n-slate-10">
        {{ t('TELETEL_ADVISOR.DISCLAIMER') }}
      </p>
    </div>
  </div>
</template>
